<template>
  <transition name="site-drawer">
    <div class="site-drawer-wrapper" v-if="drawerVisible">
      <div class="site-drawer-overlay" @click="closeDrawer" />
      <div class="site-drawer">
        <!-- Header -->
        <div class="site-drawer__header">
          <IconSet name="target" />
          <span class="site-drawer__title">{{ title }}</span>
          <button class="site-drawer__close" @click="closeDrawer" aria-label="关闭">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <!-- Tabs -->
        <div class="site-drawer__tabs">
          <div
            v-for="tab in tabs"
            :key="tab.value"
            class="site-drawer__tab"
            :class="{ active: activeTab === tab.value }"
            @click="switchTab(tab.value)"
          >
            {{ tab.label }} ({{ tab.count }})
          </div>
        </div>

        <!-- Table -->
        <div class="site-drawer__body">
          <div class="site-drawer__table-wrapper" v-loading="loading" element-loading-background="rgba(10, 22, 40, 0.7)">
            <table class="site-table">
              <thead>
                <tr>
                  <th class="col-index">#</th>
                  <th class="col-name">监测点名称</th>
                  <th class="col-risk">隐患等级</th>
                  <th class="col-action">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in tableData" :key="row.id || idx">
                  <td class="col-index">
                    <IndexBadge>{{ (pageNum - 1) * pageSize + idx + 1 }}</IndexBadge>
                  </td>
                  <td class="col-name" :title="row.siteName">{{ row.siteName }}</td>
                  <td class="col-risk">
                    <span class="risk-level-badge" :class="'risk-' + (row.riskLevelName || 'low')">{{ row.riskLevelName || '-' }}</span>
                  </td>
                  <td class="col-action">
                    <button class="site-drawer__btn" @click="goDetail(row)">进入</button>
                  </td>
                </tr>
                <tr v-if="!loading && tableData.length === 0">
                  <td colspan="4" class="col-empty">暂无数据</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="site-drawer__pagination" v-if="total > 0">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="pageNum"
              :page-sizes="[10, 20, 50]"
              :page-size="pageSize"
              :total="total"
              layout="total, sizes, prev, pager, next, jumper"
              background
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { getMonitorSitePageApi } from '@/scripts/home'
import IconSet from './IconSet.vue'
import IndexBadge from './IndexBadge.vue'

export default {
  components: { IconSet, IndexBadge },
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: '监测点列表' },
    tabs: { type: Array, default: () => [] },
    defaultTabIndex: { type: Number, default: 0 },
    deptId: { type: [Number, String], default: null }
  },
  data() {
    return {
      drawerVisible: false,
      activeTab: '',
      tableData: [],
      pageNum: 1,
      pageSize: 10,
      total: 0,
      loading: false
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.drawerVisible = val
        if (val) {
          var tab = this.tabs[this.defaultTabIndex]; this.activeTab = (tab && tab.value) || ''
          if (this.activeTab) this.fetchData()
        }
      }
    },
    tabs: {
      deep: true,
      handler() {
        if (this.drawerVisible) {
          var tab = this.tabs[this.defaultTabIndex]; this.activeTab = (tab && tab.value) || ''
          this.pageNum = 1
          if (this.activeTab) this.fetchData()
        }
      }
    }
  },
  methods: {
    closeDrawer() {
      this.drawerVisible = false
      this.$emit('update:visible', false)
    },

    switchTab(value) {
      this.activeTab = value
      this.pageNum = 1
      this.fetchData()
    },

    handleTabClick() {
      this.pageNum = 1
      this.fetchData()
    },

    handleSizeChange(size) {
      this.pageSize = size
      this.pageNum = 1
      this.fetchData()
    },

    handleCurrentChange(page) {
      this.pageNum = page
      this.fetchData()
    },

    fetchData() {
      const tab = this.tabs.find(t => t.value === this.activeTab)
      if (!tab) return
      this.loading = true
      // ponytail: riskLevel=单值精确匹配, containsRiskLevels=多选, excludeRiskLevels=排除(与containsRiskLevels配合)
      var params = {
        deptId: this.deptId,
        pageNum: this.pageNum,
        showNum: this.pageSize
      }
      if (tab.riskLevel != null) {
        params.riskLevel = tab.riskLevel
      }
      if (tab.containsRiskLevels) {
        params.containsRiskLevels = tab.containsRiskLevels
      }
      if (tab.excludeRiskLevels) {
        params.excludeRiskLevels = tab.excludeRiskLevels
      }
      getMonitorSitePageApi(params).then(res => {
        if (res.data) {
          this.tableData = res.data.dataList || []
          this.total = res.data.totalNum || 0
        } else {
          this.tableData = []
          this.total = 0
        }
      }).catch(() => {
        this.tableData = []
        this.total = 0
      }).finally(() => {
        this.loading = false
      })
    },

    goDetail(row) {
      // ponytail: 看板页无 ConsoleHeader，提前将 tab 信息写入 localStorage
      // ConsoleHeader mount 时会从 localStorage 恢复 tab 并设置 sessionStorage
      var newTabName = Date.now() + ''
      var title = row.siteName + '详情'
      var savedTabs = JSON.parse(localStorage.getItem('editableTabs') || '[]')
      savedTabs.push({ title: title, name: newTabName, branchId: row.branchId, id: row.id, router: '/console/sites/detail' })
      localStorage.setItem('editableTabs', JSON.stringify(savedTabs))
      localStorage.setItem('editableTabsValue', newTabName)
      this.$router.push({ path: '/console/sites/detail', query: { siteId: row.id, branchId: row.branchId } })
    }
  }
}
</script>

<style lang="scss" scoped>
// ponytail: v4 glass-morphism drawer for monitoring site list — matches GlassPopup's visual language

.site-drawer-wrapper {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
}

.site-drawer-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.site-drawer {
  position: relative;
  width: 38%;
  min-width: 440px;
  max-width: 560px;
  height: 100%;
  background: rgba(10, 22, 40, 0.9);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-left: 1px solid rgba(42, 98, 255, 0.12);
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 40px rgba(0, 0, 0, 0.4);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  // ponytail: premium v4 branding — blue accent glass panel
}

// Header
.site-drawer__header {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 24px;
  color: var(--color-dark-text-accent, #8AB4FF);
  font-size: 15px;
  font-family: "AlibabaPuHuiTi-2-65-Medium", sans-serif;
  border-bottom: 1px solid rgba(42, 98, 255, 0.1);
  flex-shrink: 0;
}

.site-drawer__close {
  margin-left: auto;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.35);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  padding: 0;

  &:hover {
    color: rgba(255, 255, 255, 0.85);
    background: rgba(255, 68, 68, 0.12);
  }
}

// Custom tabs (matching v4 aesthetic)
.site-drawer__tabs {
  display: flex;
  gap: 0;
  padding: 0 24px;
  border-bottom: 1px solid rgba(42, 98, 255, 0.08);
  flex-shrink: 0;
}

.site-drawer__tab {
  padding: 10px 20px;
  font-size: 13px;
  font-family: "AlibabaPuHuiTi-2-55-Regular", sans-serif;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }

  &.active {
    color: var(--color-dark-text-accent, #8AB4FF);
    font-family: "AlibabaPuHuiTi-2-65-Medium", sans-serif;
    border-bottom-color: var(--color-dark-accent, #2A62FF);
  }
}

// Body
.site-drawer__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 16px 24px 20px;
  overflow: hidden;
}

.site-drawer__table-wrapper {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

// Custom dark-theme table
.site-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: rgba(42, 98, 255, 0.2);
    color: var(--color-dark-text-accent, #8AB4FF);
    font-family: "AlibabaPuHuiTi-2-65-Medium", sans-serif;
    font-weight: normal;
    text-align: left;
    padding: 8px 10px;
    white-space: nowrap;
    border-bottom: 1px solid rgba(42, 98, 255, 0.15);
  }

  tbody tr {
    transition: background 0.15s ease;

    &:hover {
      background: rgba(42, 98, 255, 0.06);
    }
  }

  tbody td {
    padding: 8px 10px;
    color: rgba(208, 222, 238, 0.85);
    border-bottom: 1px solid rgba(108, 128, 151, 0.18);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 0;
  }

  .col-index {
    width: 50px;
    text-align: center;
  }
  .col-name { width: auto; min-width: 120px; }
  .col-risk { width: 90px; }
  .col-action { width: 70px; text-align: center; }
  .col-empty {
    text-align: center;
    padding: 40px 0;
    color: rgba(255, 255, 255, 0.3);
    font-family: "AlibabaPuHuiTi-2-55-Regular", sans-serif;
  }
}

// ponytail: risk level badges — copied from existing v4 conventions
.risk-level-badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-family: "AlibabaPuHuiTi-2-65-Medium", sans-serif;
  white-space: nowrap;

  &.risk-5,
  &.risk-高,
  &.risk-high {
    background: rgba(220, 90, 90, 0.2);
    color: #DC5A5A;
  }

  &.risk-4,
  &.risk-中,
  &.risk-mid {
    background: rgba(212, 163, 74, 0.2);
    color: #D4A34A;
  }

  &.risk-3,
  &.risk-低,
  &.risk-low {
    background: rgba(111, 201, 143, 0.15);
    color: #6FC98F;
  }

  &.risk-null {
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.35);
  }
}

// Action button
.site-drawer__btn {
  background: none;
  border: 1px solid rgba(42, 98, 255, 0.3);
  color: var(--color-dark-text-accent, #8AB4FF);
  font-family: "AlibabaPuHuiTi-2-65-Medium", sans-serif;
  font-size: 12px;
  padding: 2px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(42, 98, 255, 0.15);
    border-color: rgba(42, 98, 255, 0.5);
    box-shadow: 0 0 8px rgba(42, 98, 255, 0.15);
  }
}

// Pagination (with deep overrides for dark theme)
.site-drawer__pagination {
  flex-shrink: 0;
  padding-top: 12px;
  text-align: right;
}

// Transition
.site-drawer-enter-active,
.site-drawer-leave-active {
  .site-drawer-overlay {
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .site-drawer {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

.site-drawer-enter-from,
.site-drawer-leave-to {
  .site-drawer-overlay {
    opacity: 0;
  }
  .site-drawer {
    transform: translateX(100%);
  }
}
</style>

<style lang="scss">
// ponytail: global scoped overrides for el-pagination inside the drawer
// can't be scoped because element-ui uses deep internal selectors
.site-drawer__pagination .el-pagination {
  font-family: "AlibabaPuHuiTi-2-55-Regular", sans-serif;

  &.is-background {
    .btn-prev, .btn-next, .el-pager li {
      background: rgba(42, 98, 255, 0.15);
      color: rgba(138, 180, 255, 0.8);
      border: none;
      min-width: 28px;
      border-radius: 4px;
      font-size: 12px;
      transition: all 0.2s;

      &:hover {
        background: rgba(42, 98, 255, 0.25);
        color: #8AB4FF;
      }

      &:not(.disabled).active {
        background: #2A62FF !important;
        color: #fff !important;
      }

      &.disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
    }
  }

  .el-pagination__total,
  .el-pagination__sizes,
  .el-pagination__jump {
    color: rgba(255, 255, 255, 0.45);
    font-size: 12px;
  }

  .el-select .el-input .el-input__inner {
    background: rgba(42, 98, 255, 0.1);
    color: rgba(208, 222, 238, 0.8);
    border-color: rgba(42, 98, 255, 0.2);
  }

  .el-select-dropdown__item.selected {
    color: #2A62FF;
  }
}
</style>
